import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  Card,
  Row,
  Col,
  Form,
  Button,
  InputGroup,
  Table,
  Spinner,
  Pagination,
} from "react-bootstrap";

import {
  Download,
  RefreshCcw,
  Search,
  ChevronUp,
  ChevronDown,
} from "react-feather";

import * as XLSX from "xlsx";

import {
  fetchWardsThunk,
} from "../../../store/slices/wardsSlice";

import {
  fetchAgentsThunk,
} from "../../../store/slices/agentsSlice";

import {
  fetchWaterBillReportThunk,
  clearWaterBillReport,
} from "../../../store/slices/waterBillReportSlice";

import "./ReportPage.css";

const WaterBillReportPage = () => {
  const dispatch = useDispatch();

  // =====================================================
  // REDUX
  // =====================================================

  const wards = useSelector(
    (state) =>
      state.wards?.list || [],
  );

  const agents = useSelector(
    (state) =>
      state.agents?.list || [],
  );

  const {
    rows = [],
    loading = false,
    error = null,
  } = useSelector(
    (state) =>
      state.waterBillReport || {},
  );

  // =====================================================
  // FILTER STATE
  // =====================================================

  const [
    fromDate,
    setFromDate,
  ] = useState("");

  const [
    toDate,
    setToDate,
  ] = useState("");

  const [
    wardId,
    setWardId,
  ] = useState("");

  const [
    agentId,
    setAgentId,
  ] = useState("");

  const [
    search,
    setSearch,
  ] = useState("");

  // =====================================================
  // SORT STATE
  // =====================================================

  const [
    sortField,
    setSortField,
  ] = useState("");

  const [
    sortDirection,
    setSortDirection,
  ] = useState("asc");

  // =====================================================
  // PAGINATION
  // =====================================================

  const [
    page,
    setPage,
  ] = useState(1);

  const [
    pageSize,
    setPageSize,
  ] = useState(20);

  // =====================================================
  // EXPORT
  // =====================================================

  const [
    exportLoading,
    setExportLoading,
  ] = useState(false);

  // =====================================================
  // REPORT COLUMNS
  //
  // IMPORTANT:
  // Backend response:
  //
  // total_amount
  // old_due
  // water_net_total
  //
  // NOT:
  // water_total_amount
  // net_total
  // =====================================================

  const reportColumns = [
    {
      key: "serial",
      sortKey: "wb_id",
      label: "#",
      excelWidth: 8,
      pdfWidth: "3%",
    },

    {
      key: "qr_code",
      sortKey: "qr_code",
      label: "House No",
      excelWidth: 16,
      pdfWidth: "5%",
    },

    {
      key: "family_head",
      sortKey: "family_head",
      label: "Name",
      excelWidth: 32,
      pdfWidth: "11%",
      kannada: true,
    },

    {
      key: "mtr_type",
      sortKey: "mtr_type",
      label: "Mtr Type",
      excelWidth: 16,
      pdfWidth: "5%",
    },

    {
      key: "ward_name",
      sortKey: "ward_name",
      label: "Ward",
      excelWidth: 18,
      pdfWidth: "5%",
      kannada: true,
    },

    {
      key: "ag_name",
      sortKey: "ag_name",
      label: "Agent",
      excelWidth: 18,
      pdfWidth: "5%",
      kannada: true,
    },

    {
      key: "old_reading",
      sortKey: "old_reading",
      label: "Old Reading",
      excelWidth: 14,
      pdfWidth: "4%",
      numeric: true,
    },

    {
      key: "new_reading",
      sortKey: "new_reading",
      label: "New Reading",
      excelWidth: 14,
      pdfWidth: "4%",
      numeric: true,
    },

    {
      key: "bill_date",
      sortKey: "bill_date",
      label: "Bill Date",
      excelWidth: 16,
      pdfWidth: "5%",
    },

    {
      key: "disconnection_date",
      sortKey: "disconnection_date",
      label: "Disc Date",
      excelWidth: 18,
      pdfWidth: "5%",
    },

    {
      key: "tot_days",
      sortKey: "tot_days",
      label: "Days",
      excelWidth: 10,
      pdfWidth: "3%",
      numeric: true,
    },

    {
      key: "total_units",
      sortKey: "total_units",
      label: "Units",
      excelWidth: 10,
      pdfWidth: "3%",
      numeric: true,
    },

    {
      key: "min_units",
      sortKey: "min_units",
      label: "Min Units",
      excelWidth: 12,
      pdfWidth: "4%",
      numeric: true,
    },

    {
      key: "minimum_amt",
      sortKey: "minimum_amt",
      label: "Min Amt",
      excelWidth: 12,
      pdfWidth: "4%",
      numeric: true,
    },

    {
      key: "extra_usage",
      sortKey: "extra_usage",
      label: "Extra Use",
      excelWidth: 12,
      pdfWidth: "4%",
      numeric: true,
    },

    {
      key: "extra_amt",
      sortKey: "extra_amt",
      label: "Extra Amt",
      excelWidth: 12,
      pdfWidth: "4%",
      numeric: true,
    },

    {
      key: "other_fee",
      sortKey: "other_fee",
      label: "Other",
      excelWidth: 12,
      pdfWidth: "4%",
      numeric: true,
    },

    {
      key: "disc_charge",
      sortKey: "disc_charge",
      label: "Disconnection Charges",
      excelWidth: 20,
      pdfWidth: "5%",
      numeric: true,
    },

    {
      key: "rec_charge",
      sortKey: "rec_charge",
      label: "Reconnection Charges",
      excelWidth: 20,
      pdfWidth: "5%",
      numeric: true,
    },

    {
      key: "reading_fee",
      sortKey: "reading_fee",
      label: "Reading Fee",
      excelWidth: 14,
      pdfWidth: "4%",
      numeric: true,
    },

    // ✅ FIXED
    {
      key: "total_amount",
      sortKey: "total_amount",
      label: "Total",
      excelWidth: 14,
      pdfWidth: "4%",
      numeric: true,
    },

    {
      key: "old_due",
      sortKey: "old_due",
      label: "Old Due",
      excelWidth: 14,
      pdfWidth: "4%",
      numeric: true,
    },

    // ✅ FIXED
    {
      key: "water_net_total",
      sortKey: "water_net_total",
      label: "Net Total",
      excelWidth: 14,
      pdfWidth: "4%",
      numeric: true,
    },

    {
      key: "slrm_fee",
      sortKey: "slrm_fee",
      label: "SLRM Fee",
      excelWidth: 14,
      pdfWidth: "4%",
      numeric: true,
    },

    {
      key: "slrm_due",
      sortKey: "slrm_due",
      label: "SLRM Due",
      excelWidth: 14,
      pdfWidth: "4%",
      numeric: true,
    },

    {
      key: "slrm_total",
      sortKey: "slrm_total",
      label: "SLRM Total",
      excelWidth: 14,
      pdfWidth: "4%",
      numeric: true,
    },
  ];

  // =====================================================
  // LOAD MASTER DATA
  // =====================================================

  useEffect(() => {
    dispatch(
      fetchWardsThunk(),
    );

    dispatch(
      fetchAgentsThunk(),
    );
  }, [dispatch]);

  // =====================================================
  // DATE FORMAT
  // =====================================================

  const formatDDMMYYYY = (
    value,
  ) => {
    if (!value) {
      return "";
    }

    const [
      yyyy,
      mm,
      dd,
    ] = value.split("-");

    return `${dd}-${mm}-${yyyy}`;
  };

  // =====================================================
  // DISPLAY DATE
  // API can return ISO date
  // =====================================================

  const formatApiDate = (
    value,
  ) => {
    if (!value) {
      return "";
    }

    const date =
      new Date(value);

    if (
      Number.isNaN(
        date.getTime(),
      )
    ) {
      return value;
    }

    const dd =
      String(
        date.getDate(),
      ).padStart(
        2,
        "0",
      );

    const mm =
      String(
        date.getMonth() + 1,
      ).padStart(
        2,
        "0",
      );

    const yyyy =
      date.getFullYear();

    return `${dd}-${mm}-${yyyy}`;
  };

  // =====================================================
  // GET COLUMN VALUE
  // =====================================================

  const getColumnValue = (
    row,
    column,
    index,
  ) => {
    if (
      column.key ===
      "serial"
    ) {
      return index + 1;
    }

    if (
      column.key ===
        "bill_date" ||
      column.key ===
        "disconnection_date"
    ) {
      return formatApiDate(
        row?.[
          column.key
        ],
      );
    }

    return (
      row?.[
        column.key
      ] ?? ""
    );
  };

  // =====================================================
  // SORT
  // =====================================================

  const handleSort = (
    field,
  ) => {
    setSortField(
      (
        currentField,
      ) => {
        if (
          currentField ===
          field
        ) {
          setSortDirection(
            (
              currentDirection,
            ) =>
              currentDirection ===
              "asc"
                ? "desc"
                : "asc",
          );

          return currentField;
        }

        setSortDirection(
          "asc",
        );

        return field;
      },
    );

    setPage(1);
  };

  // =====================================================
  // SORT ICON
  // =====================================================

  const renderSortIcon = (
    field,
  ) => {
    if (
      sortField !== field
    ) {
      return (
        <span className="ms-1 report-sort-default-icon">
          <ChevronUp
            size={11}
          />

          <ChevronDown
            size={11}
          />
        </span>
      );
    }

    return sortDirection ===
      "asc" ? (
      <ChevronUp
        size={14}
        className="ms-1"
      />
    ) : (
      <ChevronDown
        size={14}
        className="ms-1"
      />
    );
  };

  // =====================================================
  // SEARCH + SORT
  // =====================================================

  const filteredRows =
    useMemo(() => {
      const query =
        search
          .trim()
          .toLowerCase();

      const searchedRows =
        !query
          ? [...rows]
          : rows.filter(
              (row) => {
                // ✅ FIXED
                // total_amount
                // water_net_total

                const searchableText = `
                  ${row.wb_id || ""}
                  ${row.qr_code || ""}
                  ${row.family_head || ""}
                  ${row.mtr_type || ""}
                  ${row.ward_name || ""}
                  ${row.ag_name || ""}
                  ${row.old_reading ?? ""}
                  ${row.new_reading ?? ""}
                  ${row.bill_date || ""}
                  ${row.disconnection_date || ""}
                  ${row.tot_days ?? ""}
                  ${row.total_units ?? ""}
                  ${row.min_units ?? ""}
                  ${row.minimum_amt ?? ""}
                  ${row.extra_usage ?? ""}
                  ${row.extra_amt ?? ""}
                  ${row.other_fee ?? ""}
                  ${row.disc_charge ?? ""}
                  ${row.rec_charge ?? ""}
                  ${row.reading_fee ?? ""}
                  ${row.total_amount ?? ""}
                  ${row.old_due ?? ""}
                  ${row.water_net_total ?? ""}
                  ${row.slrm_fee ?? ""}
                  ${row.slrm_due ?? ""}
                  ${row.slrm_total ?? ""}
                `.toLowerCase();

                return searchableText.includes(
                  query,
                );
              },
            );

      if (!sortField) {
        return searchedRows;
      }

      // =================================================
      // NUMERIC FIELDS
      // =================================================

      const numericFields =
        reportColumns
          .filter(
            (column) =>
              column.numeric,
          )
          .map(
            (column) =>
              column.sortKey,
          );

      const dateFields = [
        "bill_date",
        "disconnection_date",
      ];

      return [
        ...searchedRows,
      ].sort(
        (
          a,
          b,
        ) => {
          let aValue =
            a?.[
              sortField
            ];

          let bValue =
            b?.[
              sortField
            ];

          // =============================================
          // NUMERIC
          // =============================================

          if (
            numericFields.includes(
              sortField,
            )
          ) {
            aValue =
              Number(
                aValue ||
                  0,
              );

            bValue =
              Number(
                bValue ||
                  0,
              );

            return sortDirection ===
              "asc"
              ? aValue -
                  bValue
              : bValue -
                  aValue;
          }

          // =============================================
          // DATE
          // =============================================

          if (
            dateFields.includes(
              sortField,
            )
          ) {
            const aTime =
              aValue
                ? new Date(
                    aValue,
                  ).getTime()
                : 0;

            const bTime =
              bValue
                ? new Date(
                    bValue,
                  ).getTime()
                : 0;

            return sortDirection ===
              "asc"
              ? aTime -
                  bTime
              : bTime -
                  aTime;
          }

          // =============================================
          // TEXT
          // =============================================

          const comparison =
            String(
              aValue ||
                "",
            ).localeCompare(
              String(
                bValue ||
                  "",
              ),
              undefined,
              {
                numeric: true,
                sensitivity:
                  "base",
              },
            );

          return sortDirection ===
            "asc"
            ? comparison
            : -comparison;
        },
      );
    }, [
      rows,
      search,
      sortField,
      sortDirection,
    ]);

  // =====================================================
  // RESET PAGE
  // =====================================================

  useEffect(() => {
    setPage(1);
  }, [
    fromDate,
    toDate,
    wardId,
    agentId,
    search,
    rows.length,
  ]);

  // =====================================================
  // PAGINATION
  // =====================================================

  const total =
    filteredRows.length;

  const totalPages =
    Math.max(
      1,
      Math.ceil(
        total /
          pageSize,
      ),
    );

  const currentPage =
    Math.min(
      page,
      totalPages,
    );

  const pagedRows =
    useMemo(() => {
      const start =
        (
          currentPage -
          1
        ) *
        pageSize;

      return filteredRows.slice(
        start,
        start +
          pageSize,
      );
    }, [
      filteredRows,
      currentPage,
      pageSize,
    ]);

  // =====================================================
  // LOAD REPORT
  // =====================================================

  const handleLoad = () => {
    dispatch(
      fetchWaterBillReportThunk({
        from_date:
          fromDate,

        to_date:
          toDate,

        ward_id:
          wardId,

        agent_id:
          agentId,
      }),
    );
  };

  // =====================================================
  // CLEAR
  // =====================================================

  const handleClear = () => {
    setFromDate("");
    setToDate("");
    setWardId("");
    setAgentId("");
    setSearch("");

    setSortField("");
    setSortDirection(
      "asc",
    );

    setPage(1);

    dispatch(
      clearWaterBillReport(),
    );
  };

  // =====================================================
  // EXCEL
  // =====================================================

  const exportExcel = () => {
    if (
      exportLoading ||
      !filteredRows.length
    ) {
      return;
    }

    try {
      setExportLoading(
        true,
      );

      const data =
        filteredRows.map(
          (
            row,
            index,
          ) => {
            const exportRow =
              {};

            reportColumns.forEach(
              (column) => {
                let value =
                  getColumnValue(
                    row,
                    column,
                    index,
                  );

                if (
                  column.numeric
                ) {
                  const number =
                    Number(
                      value,
                    );

                  value =
                    Number.isFinite(
                      number,
                    )
                      ? number
                      : 0;
                }

                exportRow[
                  column.label
                ] =
                  value;
              },
            );

            return exportRow;
          },
        );

      const worksheet =
        XLSX.utils.json_to_sheet(
          data,
        );

      worksheet[
        "!cols"
      ] =
        reportColumns.map(
          (column) => ({
            wch:
              column.excelWidth ||
              15,
          }),
        );

      const workbook =
        XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Water Bill Report",
      );

      XLSX.writeFile(
        workbook,
        "water_bill_report.xlsx",
      );
    } catch (
      exportError
    ) {
      console.error(
        "Excel export failed:",
        exportError,
      );
    } finally {
      setExportLoading(
        false,
      );
    }
  };

  // =====================================================
  // PDF
  // =====================================================

  const exportPDF = () => {
    if (
      exportLoading ||
      !filteredRows.length
    ) {
      return;
    }

    try {
      setExportLoading(
        true,
      );

      const printWindow =
        window.open(
          "",
          "_blank",
          "width=1800,height=1000",
        );

      if (!printWindow) {
        alert(
          "Please allow pop-ups to generate the PDF.",
        );

        return;
      }

      // =================================================
      // ESCAPE
      // =================================================

      const escapeHtml =
        (value) => {
          if (
            value ===
              null ||
            value ===
              undefined
          ) {
            return "";
          }

          return String(
            value,
          )
            .replace(
              /&/g,
              "&amp;",
            )
            .replace(
              /</g,
              "&lt;",
            )
            .replace(
              />/g,
              "&gt;",
            )
            .replace(
              /"/g,
              "&quot;",
            )
            .replace(
              /'/g,
              "&#039;",
            );
        };

      // =================================================
      // HEADER
      // =================================================

      const tableHead =
        reportColumns
          .map(
            (
              column,
            ) => `
              <th
                style="
                  width:
                  ${column.pdfWidth};
                "
              >
                ${escapeHtml(
                  column.label,
                )}
              </th>
            `,
          )
          .join("");

      // =================================================
      // BODY
      // =================================================

      const tableBody =
        filteredRows
          .map(
            (
              row,
              index,
            ) => {
              const cells =
                reportColumns
                  .map(
                    (
                      column,
                    ) => {
                      const value =
                        getColumnValue(
                          row,
                          column,
                          index,
                        );

                      const classes =
                        [];

                      if (
                        column.numeric
                      ) {
                        classes.push(
                          "number-cell",
                        );
                      }

                      if (
                        column.kannada
                      ) {
                        classes.push(
                          "kannada-cell",
                        );
                      }

                      if (
                        column.key ===
                        "serial"
                      ) {
                        classes.push(
                          "center-cell",
                        );
                      }

                      return `
                        <td
                          class="
                            ${classes.join(
                              " ",
                            )}
                          "
                        >
                          ${escapeHtml(
                            value,
                          )}
                        </td>
                      `;
                    },
                  )
                  .join("");

              return `
                <tr>
                  ${cells}
                </tr>
              `;
            },
          )
          .join("");

      // =================================================
      // PRINT DOCUMENT
      // =================================================

      printWindow.document.open();

      printWindow.document.write(`
        <!DOCTYPE html>

        <html lang="kn">

        <head>

          <meta charset="UTF-8" />

          <title>
            Water Bill - Report
          </title>

          <style>

            @page {
              size:
                A4 landscape;

              margin:
                5mm;
            }

            * {
              box-sizing:
                border-box;
            }

            html,
            body {
              margin: 0;
              padding: 0;

              background:
                #ffffff;

              color:
                #000000;
            }

            body {
              font-family:
                "Noto Sans Kannada",
                "Nirmala UI",
                "Tunga",
                Arial,
                sans-serif;

              font-size:
                6px;

              -webkit-print-color-adjust:
                exact;

              print-color-adjust:
                exact;
            }

            .report-container {
              width:
                100%;
            }

            .report-title {
              margin-bottom:
                3px;

              font-family:
                Arial,
                sans-serif;

              font-size:
                13px;

              font-weight:
                700;
            }

            .report-meta {
              margin-bottom:
                6px;

              font-family:
                Arial,
                sans-serif;

              font-size:
                7px;
            }

            table {
              width:
                100%;

              border-collapse:
                collapse;

              table-layout:
                fixed;
            }

            thead {
              display:
                table-header-group;
            }

            tr {
              break-inside:
                avoid;

              page-break-inside:
                avoid;
            }

            th {
              border:
                1px solid #999;

              padding:
                3px 1px;

              background:
                #eeeeee;

              font-family:
                Arial,
                sans-serif;

              font-size:
                5.5px;

              font-weight:
                700;

              line-height:
                1.15;

              text-align:
                center;

              vertical-align:
                middle;

              overflow-wrap:
                break-word;
            }

            td {
              border:
                1px solid
                #cccccc;

              padding:
                2px 1px;

              font-family:
                "Noto Sans Kannada",
                "Nirmala UI",
                "Tunga",
                Arial,
                sans-serif;

              font-size:
                5.5px;

              line-height:
                1.35;

              vertical-align:
                middle;

              white-space:
                normal;

              word-break:
                normal;

              overflow-wrap:
                break-word;

              text-rendering:
                optimizeLegibility;
            }

            .kannada-cell {
              font-family:
                "Noto Sans Kannada",
                "Nirmala UI",
                "Tunga",
                Arial,
                sans-serif;

              font-size:
                6px;

              line-height:
                1.45;
            }

            .number-cell {
              text-align:
                right;
            }

            .center-cell {
              text-align:
                center;
            }

          </style>

        </head>

        <body>

          <div class="report-container">

            <div class="report-title">
              Water Bill - Report
            </div>

            <div class="report-meta">

              Total Records:
              ${filteredRows.length}

              ${
                fromDate
                  ? `
                    &nbsp;&nbsp;
                    From:
                    ${escapeHtml(
                      formatDDMMYYYY(
                        fromDate,
                      ),
                    )}
                  `
                  : ""
              }

              ${
                toDate
                  ? `
                    &nbsp;&nbsp;
                    To:
                    ${escapeHtml(
                      formatDDMMYYYY(
                        toDate,
                      ),
                    )}
                  `
                  : ""
              }

            </div>

            <table>

              <thead>
                <tr>
                  ${tableHead}
                </tr>
              </thead>

              <tbody>
                ${tableBody}
              </tbody>

            </table>

          </div>

          <script>

            window.onload =
              function () {

                if (
                  document.fonts &&
                  document.fonts.ready
                ) {
                  document.fonts.ready.then(
                    function () {
                      setTimeout(
                        function () {
                          window.print();
                        },
                        300
                      );
                    }
                  );
                } else {
                  setTimeout(
                    function () {
                      window.print();
                    },
                    500
                  );
                }

              };

          <\/script>

        </body>

        </html>
      `);

      printWindow.document.close();
    } catch (
      exportError
    ) {
      console.error(
        "PDF export failed:",
        exportError,
      );
    } finally {
      setExportLoading(
        false,
      );
    }
  };

  // =====================================================
  // PAGINATION UI
  // =====================================================

  const renderPagination =
    () => {
      if (
        totalPages <= 1
      ) {
        return null;
      }

      const items = [];

      const maxButtons =
        5;

      let start =
        Math.max(
          1,

          currentPage -
            Math.floor(
              maxButtons /
                2,
            ),
        );

      let end =
        Math.min(
          totalPages,

          start +
            maxButtons -
            1,
        );

      start =
        Math.max(
          1,

          end -
            maxButtons +
            1,
        );

      items.push(
        <Pagination.Prev
          key="prev"
          disabled={
            currentPage ===
            1
          }
          onClick={() =>
            setPage(
              (
                previous,
              ) =>
                Math.max(
                  1,
                  previous -
                    1,
                ),
            )
          }
        />,
      );

      for (
        let pageNumber =
          start;
        pageNumber <=
        end;
        pageNumber += 1
      ) {
        items.push(
          <Pagination.Item
            key={
              pageNumber
            }
            active={
              pageNumber ===
              currentPage
            }
            onClick={() =>
              setPage(
                pageNumber,
              )
            }
          >
            {
              pageNumber
            }
          </Pagination.Item>,
        );
      }

      items.push(
        <Pagination.Next
          key="next"
          disabled={
            currentPage ===
            totalPages
          }
          onClick={() =>
            setPage(
              (
                previous,
              ) =>
                Math.min(
                  totalPages,
                  previous +
                    1,
                ),
            )
          }
        />,
      );

      return (
        <Pagination className="mb-0">
          {items}
        </Pagination>
      );
    };

  // =====================================================
  // UI
  // =====================================================

  return (
    <div className="report-page">

      <Card className="report-card">

        {/* ================================================= */}
        {/* HEADER */}
        {/* ================================================= */}

        <Card.Header className="report-card-header">

          <div className="report-header-row">

            <div className="report-title">
              Water Bill - Report
            </div>

          </div>

        </Card.Header>

        <Card.Body>

          {/* ================================================= */}
          {/* ERROR */}
          {/* ================================================= */}

          {error ? (
            <div className="alert alert-danger mb-3">
              {error}
            </div>
          ) : null}

          {/* ================================================= */}
          {/* FILTERS */}
          {/* ================================================= */}

          <div className="report-filter-box">

            <Row className="g-3">

              {/* FROM DATE */}

              <Col
                lg={3}
                md={6}
              >

                <Form.Label className="report-label">
                  From Date
                </Form.Label>

                <Form.Control
                  type="date"
                  value={
                    fromDate
                  }
                  onChange={(
                    event,
                  ) =>
                    setFromDate(
                      event
                        .target
                        .value,
                    )
                  }
                />

                <small className="text-muted">

                  {fromDate
                    ? formatDDMMYYYY(
                        fromDate,
                      )
                    : "dd-mm-yyyy"}

                </small>

              </Col>

              {/* TO DATE */}

              <Col
                lg={3}
                md={6}
              >

                <Form.Label className="report-label">
                  To Date
                </Form.Label>

                <Form.Control
                  type="date"
                  value={
                    toDate
                  }
                  onChange={(
                    event,
                  ) =>
                    setToDate(
                      event
                        .target
                        .value,
                    )
                  }
                />

                <small className="text-muted">

                  {toDate
                    ? formatDDMMYYYY(
                        toDate,
                      )
                    : "dd-mm-yyyy"}

                </small>

              </Col>

              {/* WARD */}

              <Col
                lg={3}
                md={6}
              >

                <Form.Label className="report-label">
                  Ward
                </Form.Label>

                <Form.Select
                  value={
                    wardId
                  }
                  onChange={(
                    event,
                  ) =>
                    setWardId(
                      event
                        .target
                        .value,
                    )
                  }
                >

                  <option value="">
                    Select Ward
                  </option>

                  {wards.map(
                    (
                      ward,
                    ) => (
                      <option
                        key={
                          ward
                            .ward_id
                        }
                        value={String(
                          ward
                            .ward_id,
                        )}
                      >
                        {
                          ward
                            .ward_name
                        }
                      </option>
                    ),
                  )}

                </Form.Select>

              </Col>

              {/* AGENT */}

              <Col
                lg={3}
                md={6}
              >

                <Form.Label className="report-label">
                  Agent
                </Form.Label>

                <Form.Select
                  value={
                    agentId
                  }
                  onChange={(
                    event,
                  ) =>
                    setAgentId(
                      event
                        .target
                        .value,
                    )
                  }
                >

                  <option value="">
                    Select Agent
                  </option>

                  {agents.map(
                    (
                      agent,
                    ) => (
                      <option
                        key={
                          agent
                            .agent_id
                        }
                        value={String(
                          agent
                            .agent_id,
                        )}
                      >
                        {
                          agent
                            .ag_name
                        }
                      </option>
                    ),
                  )}

                </Form.Select>

              </Col>

              {/* ACTIONS */}

              <Col
                lg={12}
                className="d-flex align-items-end"
              >

                <div className="report-filter-actions">

                  <Button
                    className="report-btn-primary"
                    variant="primary"
                    onClick={
                      handleLoad
                    }
                    disabled={
                      loading
                    }
                  >

                    {loading ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />

                        Loading
                      </>
                    ) : (
                      "Load"
                    )}

                  </Button>

                  <Button
                    className="report-btn"
                    variant="outline-secondary"
                    onClick={
                      handleClear
                    }
                    disabled={
                      loading
                    }
                  >

                    <RefreshCcw
                      size={16}
                      className="me-2"
                    />

                    Clear

                  </Button>

                </div>

              </Col>

            </Row>

          </div>

          {/* ================================================= */}
          {/* TOOLBAR */}
          {/* ================================================= */}

          <div className="report-table-toolbar">

            <div className="report-table-title">

              Water Bills{" "}

              <span className="report-count">
                (
                {total.toLocaleString()}
                {" "}
                records)
              </span>

            </div>

            <div className="report-table-right">

              {/* SEARCH */}

              <InputGroup className="report-search">

                <InputGroup.Text>
                  <Search
                    size={16}
                  />
                </InputGroup.Text>

                <Form.Control
                  value={
                    search
                  }
                  onChange={(
                    event,
                  ) => {
                    setSearch(
                      event
                        .target
                        .value,
                    );

                    setPage(1);
                  }}
                  placeholder="Search house / name / ward / agent..."
                />

              </InputGroup>

              {/* EXCEL */}

              <Button
                className="report-btn"
                variant="outline-primary"
                onClick={
                  exportExcel
                }
                disabled={
                  !filteredRows.length ||
                  exportLoading
                }
              >

                {exportLoading ? (
                  <Spinner
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                ) : (
                  <Download
                    size={16}
                    className="me-2"
                  />
                )}

                Excel

              </Button>

              {/* PDF */}

              <Button
                className="report-btn"
                variant="outline-danger"
                onClick={
                  exportPDF
                }
                disabled={
                  !filteredRows.length ||
                  exportLoading
                }
              >

                {exportLoading ? (
                  <Spinner
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                ) : (
                  <Download
                    size={16}
                    className="me-2"
                  />
                )}

                PDF

              </Button>

            </div>

          </div>

          {/* ================================================= */}
          {/* TABLE */}
          {/* ================================================= */}

          <div className="report-table-wrap">

            <div className="report-table-scroll">

              <Table
                hover
                className="mb-0 report-table"
              >

                <thead>

                  <tr>

                    {reportColumns.map(
                      (
                        column,
                      ) => (
                        <th
                          key={
                            column.key
                          }
                        >

                          <button
                            type="button"
                            className={`report-sort-btn ${
                              sortField ===
                              column.sortKey
                                ? "is-active"
                                : ""
                            }`}
                            onClick={() =>
                              handleSort(
                                column.sortKey,
                              )
                            }
                          >

                            {
                              column.label
                            }

                            {renderSortIcon(
                              column.sortKey,
                            )}

                          </button>

                        </th>
                      ),
                    )}

                  </tr>

                </thead>

                <tbody>

                  {loading ? (

                    <tr>

                      <td
                        colSpan={
                          reportColumns.length
                        }
                        className="text-center py-4"
                      >

                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />

                        Loading...

                      </td>

                    </tr>

                  ) : pagedRows.length ? (

                    pagedRows.map(
                      (
                        row,
                        index,
                      ) => {
                        const globalIndex =
                          (
                            currentPage -
                            1
                          ) *
                            pageSize +
                          index;

                        return (
                          <tr
                            key={String(
                              row.wb_id ||
                                globalIndex,
                            )}
                          >

                            {reportColumns.map(
                              (
                                column,
                              ) => (
                                <td
                                  key={
                                    column.key
                                  }
                                >
                                  {getColumnValue(
                                    row,
                                    column,
                                    globalIndex,
                                  )}
                                </td>
                              ),
                            )}

                          </tr>
                        );
                      },
                    )

                  ) : (

                    <tr>

                      <td
                        colSpan={
                          reportColumns.length
                        }
                        className="text-center py-4"
                      >
                        No data found
                      </td>

                    </tr>

                  )}

                </tbody>

              </Table>

            </div>

            {/* ================================================= */}
            {/* PAGINATION */}
            {/* ================================================= */}

            <div className="report-pagination-bar">

              <div className="report-page-size">

                <span>
                  Rows:
                </span>

                <Form.Select
                  value={
                    pageSize
                  }
                  onChange={(
                    event,
                  ) => {
                    setPage(1);

                    setPageSize(
                      Number(
                        event
                          .target
                          .value,
                      ),
                    );
                  }}
                  className="page-size-select"
                >

                  {[
                    10,
                    20,
                    50,
                    100,
                  ].map(
                    (
                      size,
                    ) => (
                      <option
                        key={
                          size
                        }
                        value={
                          size
                        }
                      >
                        {size}
                      </option>
                    ),
                  )}

                </Form.Select>

              </div>

              <div className="report-pagination">
                {renderPagination()}
              </div>

              <div className="report-page-info">

                Page{" "}
                {currentPage}
                {" "}
                /
                {" "}
                {totalPages}

              </div>

            </div>

          </div>

        </Card.Body>

      </Card>

    </div>
  );
};

export default WaterBillReportPage;